import { useQuery } from '@tanstack/react-query';
import type { PractitionerDataErrorType } from '@common/enums/practitionerDataErrorType';
import type { PaginatedListResponseBody } from '@common/interfaces/api/apiResponse';
import type {
    GetPractitionerDataErrorsResponse,
    PractitionerDataError,
} from '@common/interfaces/api/practitionerDataErrorsApi';
import { getJson } from '@frontend/store/fetch';
import { minutesToMilliseconds } from '@frontend/store/utils/minutesToMilliseconds';

const url = '/api/practitioners/dataErrors';
const queryKey = 'practitionerDataErrors';

export type PractitionerCheckFilters = {
    types: PractitionerDataErrorType[];
};

async function fetchDataErrors(): Promise<GetPractitionerDataErrorsResponse> {
    const response = await getJson<GetPractitionerDataErrorsResponse>(url);

    if (!response?.json) {
        throw new Error('Failed to fetch data errors');
    }

    return response.json;
}

export function useGetDataErrors(): {
    data: PaginatedListResponseBody<PractitionerDataError>;
    error: unknown;
    isLoading: boolean;
} {
    const {
        data: rawResponse,
        isLoading,
        error,
    } = useQuery({
        queryFn: () => fetchDataErrors(),
        queryKey: [queryKey],
        staleTime: minutesToMilliseconds(5),
    });

    if (!rawResponse) {
        return { data: { items: [], total: 0 }, error, isLoading };
    }

    const data: PaginatedListResponseBody<PractitionerDataError> = { items: rawResponse, total: rawResponse.length };

    return { data, error, isLoading };
}
